import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface Row {
  id: string;
  orderNo: string;
  status: string;
  receiverName: string;
  receiverAddress: string;
  createdAt: string;
  pickupPoint?: { name: string } | null;
}

interface Page {
  data: Row[];
  meta: { cursor: { next: string | null; hasMore: boolean }; total?: number };
}

const PAGE = 20;

/**
 * 已取消 —— 司机的一份**查得到的历史**。
 *
 * ★ 和别的标签不一样,它不从 `/driver/tasks` 那份当天快照里取。
 *   那份只回今天动过的单,于是「昨天那单到底是取消了还是我漏送了」
 *   这个问题在第二天就查不到答案了 —— 而这正是司机最需要问的时候
 *   (客户打电话来问的永远是昨天那一单)。
 *
 * ★ 所以这一栏走分页接口、往下滑加载更多。取消的单会一直累积,
 *   一次全拉回来在手机上是实打实的卡顿,而他要找的通常就在最前面几条。
 */
export function CancelledList({
  keyword,
  onOpenOrder,
}: {
  /**
   * 顶上搜索框里的关键词。
   *
   * ★ 这一栏的搜索**交给服务端**,不在本地过滤。
   *
   *   它是分页的(取消的单会一直累积),本地只能过滤「已经滑出来的那几页」——
   *   而司机在这儿查的恰恰是更早的单(客户打电话来问的永远是上周那一单)。
   *   本地过滤会让他得出「没有这单」的结论,而那单就在第三页。
   */
  keyword: string;
  onOpenOrder: (id: string) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  /** 已经在飞的那一页 —— 滚动事件会连着触发好几次 */
  const inflight = useRef(false);
  /**
   * 查询条件的代号。换关键词就 +1。
   *
   * ★ 为什么需要它:换关键词必须**打断**正在飞的那一页,而打断之后
   *   那一页的响应还是会回来 —— 直接 setRows 的话,新查询刚清空的列表
   *   会被旧查询的结果填回去(而且带着旧 cursor),于是屏幕上是
   *   「搜了但没过滤」。按代号对一下,过期的响应直接丢掉。
   */
  const gen = useRef(0);

  const loadMore = useCallback(
    async (reset = false) => {
      /*
       * ★ `reset`(换了关键词 / 第一次进来)**不受 inflight 阻挡**。
       *
       *   原来这里是 `if (inflight.current) return;` —— 一视同仁地挡掉。
       *   后果:司机一进「已取消」就打字,而第一页还在飞 ——
       *   这次搜索被直接丢掉,列表继续显示全部取消单,
       *   他看到的是「打了字没反应」。往下滑触发分页时同样会中。
       *   (这条是 e2e 替我发现的:列表里 37 条一条没少。)
       */
      if (!reset && inflight.current) return;
      if (!reset && !hasMore) return;
      const my = reset ? ++gen.current : gen.current;
      inflight.current = true;
      setLoading(true);
      try {
        const qs = new URLSearchParams({ status: 'CANCELLED', limit: String(PAGE) });
        // ★ 关键词进查询串 —— 服务端搜的是整张表,不只是取回来的这几页
        const kw = keyword.trim();
        if (kw) qs.set('q', kw);
        // 第一页顺便要个总数 —— 大表上 count 很贵,只算这一次
        if (reset) qs.set('withTotal', 'true');
        else if (cursor) qs.set('cursor', cursor);

        const r = await api<Page>(`/orders?${qs.toString()}`);
        // 过期的响应丢掉 —— 它属于上一个关键词
        if (my !== gen.current) return;
        setRows((prev) => (reset ? r.data : [...prev, ...r.data]));
        setCursor(r.meta.cursor.next);
        setHasMore(r.meta.cursor.hasMore);
        if (r.meta.total != null) setTotal(r.meta.total);
        setError(null);
      } catch (e) {
        /*
         * ★ 说清楚是**没网**还是真的出错。
         *   这一栏和别的栏不同:它不在离线缓存里,所以在电梯里点进来
         *   一定是空的 —— 不解释的话看起来像「取消的单都不见了」。
         */
        setError(
          !navigator.onLine
            ? '现在没网 —— 已取消这一栏要联网才查得到'
            : e instanceof ApiFailure
              ? e.message
              : '加载失败',
        );
      } finally {
        /*
         * ★ 只有**当前那一代**才能把 inflight 放掉。
         *   旧请求回来时顺手清掉的话,它会把新请求的「在飞」状态抹了 ——
         *   于是滚动又触发一次,同一页加载两遍。
         */
        if (my === gen.current) {
          setLoading(false);
          inflight.current = false;
        }
      }
    },
    [cursor, hasMore, keyword],
  );

  useEffect(() => {
    /*
     * ★ 关键词变了要**从第一页重来**,不能接着上一次的 cursor ——
     *   那个 cursor 是上一个查询条件下的位置,拿它去翻新条件的结果
     *   会漏掉前面所有匹配的单(而界面上看起来只是「搜到的比较少」)。
     */
    setCursor(null);
    setHasMore(true);
    void loadMore(true);
    // 只随关键词重拉;同一个关键词下的后续页靠滚动触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  // 滑到底就接着加载。IntersectionObserver 比监听 scroll 省电
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore();
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  return (
    <div className="mx-4 mt-4">
      <div className="flex items-center justify-between py-2 text-slate-600 dark:text-slate-400">
        <span className="font-medium">已取消{total != null ? ` (${total})` : ''}</span>
        <span className="text-sm text-slate-400">这些单不用送了,留着查</span>
      </div>

      {error && (
        <div className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800
                        dark:bg-amber-950/40 dark:text-amber-300">
          {error}
          <button onClick={() => void loadMore(true)} className="ml-2 font-medium underline">
            重试
          </button>
        </div>
      )}

      {rows.length === 0 && !loading && !error && (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-300 p-10 text-center
                        text-slate-400 dark:border-slate-700">
          {/* ★ 「一单都没取消过」和「搜不到」要分开说 —— 否则他会以为
               取消记录没了,而其实是框里还留着上次的关键词 */}
          {keyword.trim() ? `已取消里没有「${keyword.trim()}」` : '没有取消过的单'}
        </div>
      )}

      <ul className="space-y-1.5" aria-label="已取消列表">
        {rows.map((o) => (
          <li key={o.id}>
            <button
              onClick={() => onOpenOrder(o.id)}
              aria-label={`打开订单详情:${o.receiverName} ${o.orderNo}`}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-left
                         opacity-70 dark:border-slate-800"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-slate-800 dark:text-slate-200">
                  {o.receiverName}
                </span>
                <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500
                                 dark:bg-slate-800">
                  已取消
                </span>
              </div>
              <div className="mt-0.5 truncate text-sm text-slate-500">{o.receiverAddress}</div>
              <div className="mt-0.5 flex gap-2 text-xs text-slate-400">
                <span className="tabular-nums">{o.orderNo}</span>
                <span>
                  {new Date(o.createdAt).toLocaleDateString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                  })}
                </span>
                {o.pickupPoint?.name && <span className="truncate">{o.pickupPoint.name}</span>}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {/* 滑到这里就加载下一页;同时留一个按钮 —— 观察器在某些机型上不触发 */}
      <div ref={sentinel} className="py-4 text-center text-sm text-slate-400">
        {loading ? (
          '加载中…'
        ) : hasMore ? (
          <button onClick={() => void loadMore()} className="underline">
            加载更多
          </button>
        ) : rows.length > 0 ? (
          '没有更多了'
        ) : null}
      </div>
    </div>
  );
}
